import { useCallback, useContext, useState } from 'react'
import { Destination, IGraphArrayPayload } from '../../context/AxiosContext'
import { isOfficeInitialized, outlookDisplayMessageForm } from '../../helpers/outlookHelper'
import { fileDownload } from '../../components/StealthLink'
import useAxios from '../../hooks/useAxios'
import { Message } from '@microsoft/microsoft-graph-types'
import { AxiosInstance } from 'axios'
import AuthorizationContext, { Host } from '../../context/AuthorizationContext'
import { toastContainerId } from '../../pages/App'
import { toast } from 'react-toastify'
import useUser from '../../hooks/useUser'

type ReturnProps = {
    setMessage: (message: Message) => void
    loaded: boolean
    isOpening: boolean
    open: () => void
    download: () => void
    openOrDownload: () => void
}

function getMessageId(axios: AxiosInstance, message: Message, internetMessageId?: string): Promise<string> {
    return axios
        .get<IGraphArrayPayload<Message>>('/messages/', {
            baseURL: Destination.Email,
            headers: { SuppressErrors: 'true' },
            params: {
                $select: 'id,webLink',
                $filter: `internetMessageId eq '${message?.internetMessageId || internetMessageId}'`,
            },
        })
        .then(response => response.data?.value?.[0]?.id)
}

function copyMessage(axios: AxiosInstance, messageId: string, host: Host, currentMailbox: string): Promise<string> {
    return axios
        .get<string>(
            `/api/copy-message?hostKey=${host.RowKey}&sourceEmail=${encodeURIComponent(host.ReplicationMail)}&messageId=${messageId}&destinationEmail=${encodeURIComponent(currentMailbox)}&destinationFolder=deleteditems`,
            {
                baseURL: process.env.INBOXER_HOST,
            },
        )
        .then(response => response.data)
}

const makeToast = (result?: Office.AsyncResult<void>, error?: Error) => {
    if (error) {
        toast(`${error.message}`, {
            autoClose: 1000,
            containerId: toastContainerId,
        })
    } else if (result?.error) {
        toast(`${result.error.code}: ${result.error.message}`, {
            autoClose: 1000,
            containerId: toastContainerId,
        })
    }
}

export default function useOpenDownloadMessage(initialMessage?: Message): ReturnProps {
    const axios = useAxios()
    const { currentMailbox } = useUser()
    const { host: currentHost } = useContext(AuthorizationContext)

    const [message, setMessage] = useState<Message>(initialMessage)
    const [isOpening, setIsOpening] = useState<boolean>(false)

    const openMessage = useCallback(() => {
        setIsOpening(true)
        outlookDisplayMessageForm(message.id, (result, error) => {
            if (result?.status === Office.AsyncResultStatus.Failed) {
                console.log(`Couldn't load shared e-mail: `, error.message)
                displayUserMessage()
            } else {
                setIsOpening(false)
            }
        })
    }, [message])

    const displayUserMessage = useCallback(() => {
        getMessageId(axios, message)
            .then(id => {
                if (id) {
                    outlookDisplayMessageForm(message.id, (result, error) => {
                        if (result?.status === Office.AsyncResultStatus.Failed) {
                            console.log(`Couldn't load user e-mail: `, error.message)
                            copyAndDisplayMessage()
                        } else {
                            setIsOpening(false)
                        }
                    })
                } else {
                    console.log(`Couldn't find e-mail in users mailbox`)
                    copyAndDisplayMessage()
                }
            })
            .catch(error => {
                console.log(`Couldn't load user e-mail: `, error.message)
                copyAndDisplayMessage()
            })
    }, [message])

    const copyAndDisplayMessage = useCallback(() => {
        copyMessage(axios, message.id, currentHost, currentMailbox)
            .then(id => {
                if (id) {
                    outlookDisplayMessageForm(id[0], result => {
                        if (result?.status === Office.AsyncResultStatus.Failed) {
                            outlookDisplayMessageForm(id[1], (result, error) => {
                                if (result?.status === Office.AsyncResultStatus.Failed) {
                                    console.log(`Couldn't load copy of e-mail: `, error.message)
                                    setIsOpening(false)
                                    makeToast(undefined, { name: '', message: 'Kon de e-mail niet openen.' })
                                } else {
                                    setIsOpening(false)
                                }
                            })
                        } else {
                            setIsOpening(false)
                        }
                    })
                } else {
                    setIsOpening(false)
                    makeToast(undefined, { name: '', message: 'Kon de e-mail niet openen.' })
                }
            })
            .catch(error => {
                console.log(`Couldn't load user e-mail: `, error.message)
                setIsOpening(false)
                makeToast(undefined, { name: '', message: 'Kon de e-mail niet openen.' })
            })
    }, [message])

    const downloadMessage = useCallback(() => {
        setIsOpening(true)
        axios
            .get<Blob>(`/messages/${message.id}/$value`, {
                responseType: 'blob',
                baseURL: Destination.ReplicationEmail,
            })
            .then(response => {
                console.log('Serving e-mail as download:', message.id)
                fileDownload(response.data, `${message.id}.eml`)
                setIsOpening(false)
            })
            .catch(error => {
                setIsOpening(false)
                makeToast(undefined, error as Error)
            })
    }, [message])

    const openOrDownloadMessage = useCallback(() => {
        if (isOfficeInitialized()) {
            openMessage()
        } else {
            downloadMessage()
        }
    }, [message])

    return {
        setMessage,
        loaded: message !== undefined,
        isOpening,
        open: openMessage,
        download: downloadMessage,
        openOrDownload: openOrDownloadMessage,
    }
}
